// SAB-575: /login — marketing-first sign in. Social-first. Commissioner intent
// rides in the route so "Create a League → has account → Log In" still lands in
// league setup (case 2). Already-authed users skip straight to the target.
import { authErrorCopy } from '@/lib/authErrorCopy';
import { useState } from 'react';
import { Navigate, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Check, UserCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AuthEntryLayout } from '@/components/auth/AuthEntryLayout';
import { Capacitor } from '@capacitor/core';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { fetchProviderHint, providerHintMessage } from '@/lib/authProviderHint';
import { NATIVE_OAUTH_MIN_VERSION } from '@/lib/nativeOAuthGate';
import { useOAuthSignIn } from '@/hooks/useOAuthSignIn';
import { resolveAuthTarget, authHref } from '@/lib/landingAuthCopy';

const FIELD =
  'h-[52px] w-full rounded-xl px-4 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none focus:ring-2 focus:ring-ring';
const FIELD_STYLE = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)' } as const;
const LABEL = 'text-[12.5px] font-semibold text-white/55';

export default function Login() {
  const { user, isLoading, signIn } = useAuth();
  const { atLeast } = useNativeAppVersion();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { toast } = useToast();

  const intent = params.get('intent');
  // SAB-592: an invite link routes here as /login?returnTo=/nfl/join/CODE. A valid
  // returnTo becomes the post-auth target (see resolveAuthTarget), bypassing the
  // landing default, and rides through OAuth via useOAuthSignIn(target).
  const returnTo = params.get('returnTo');
  const from = (location.state as { from?: string })?.from ?? null;
  const target = resolveAuthTarget(intent, from, returnTo);
  const signupHref = authHref('/signup', intent, returnTo);

  const { signInWith, pending: oauthPending } = useOAuthSignIn(target);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true); // checked by default (convenience)
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && user) return <Navigate to={target} replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        // SAB-977: a Google/Apple signup who types a password gets "Invalid login
        // credentials", which is true and unactionable -- there is no password to be
        // wrong. Ask the server which provider owns the address; it answers only for
        // accounts that genuinely cannot use a password, and only with the provider name.
        // Null (unknown address, password-capable account, or rate limited -- the server
        // cannot tell them apart) falls through to the ordinary message unchanged.
        const label = await fetchProviderHint(email);
        toast({
          title: 'Sign in failed',
          // SAB-977's provider hint wins when the server could identify the account;
          // otherwise SAB-985 classified copy, never the raw message.
          description: label ? providerHintMessage(label) : authErrorCopy(error, 'signin').description,
          variant: 'destructive',
        });
        return;
      }
      navigate(target, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async () => {
    if (!email) { toast({ title: 'Enter your email first', description: 'Type your email above, then tap Forgot?' }); return; }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) console.error('[auth] reset email failed:', authErrorCopy(error, 'reset').detail);
    toast(error
      ? { ...authErrorCopy(error, 'reset'), variant: 'destructive' as const }
      : { title: 'Reset link sent', description: `Check ${email} for a password reset link.` });
  };

  // Biometric login is disabled until secure storage lands (SAB-232). The button
  // is present per the design; wired to a clear placeholder for now. FLAG in report.
  const handleFaceId = () => {
    toast({ title: 'Face ID coming soon', description: 'Biometric sign-in returns with secure storage. Use email or a social login for now.' });
  };

  return (
    <AuthEntryLayout
      headline="Welcome back"
      subhead="Your leagues are where you left them."
      headerPrompt="New here?"
      headerLinkLabel="Sign Up"
      headerLinkTo={signupHref}
    >
      <SocialAuthButtons onProvider={signInWith} disabled={oauthPending || submitting} />

      {/* SAB (native OAuth gate): gate the "or" divider too so it isn't orphaned when
          SocialAuthButtons returns null on native. On native the form follows the
          header directly. */}
      {(!Capacitor.isNativePlatform() || atLeast(NATIVE_OAUTH_MIN_VERSION)) && (
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="li-email" className={`${LABEL} block mb-1.5`}>Email</label>
          <input id="li-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com" className={FIELD} style={FIELD_STYLE} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="li-password" className={LABEL}>Password</label>
            <button type="button" onClick={handleForgot} className="text-[12.5px] font-semibold text-[hsl(var(--primary))] hover:underline">Forgot?</button>
          </div>
          <div className="relative">
            <input id="li-password" type={showPw ? 'text' : 'password'} required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••"
              className={`${FIELD} pr-11`} style={FIELD_STYLE} />
            <button type="button" onClick={() => setShowPw(!showPw)} tabIndex={-1}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Keep me signed in — checked by default (convenience, not consent).
            Supabase persists the session by default; this is presentational for now. */}
        <button type="button" onClick={() => setKeepSignedIn(!keepSignedIn)}
          className="flex items-center gap-2.5 text-left">
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors"
            style={keepSignedIn
              ? { background: 'hsl(var(--primary))' }
              : { background: 'transparent', border: '1px solid rgba(255,255,255,0.24)' }}
          >
            {keepSignedIn && <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />}
          </span>
          <span className="text-[14px] text-muted-foreground">Keep me signed in</span>
        </button>

        <button type="submit" disabled={submitting || oauthPending}
          className="h-[54px] w-full rounded-xl font-display font-bold text-[17px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="h-5 w-5 animate-spin" />Signing in…</> : 'Log In'}
        </button>

        <button type="button" onClick={handleFaceId}
          className="h-[52px] w-full rounded-xl font-semibold text-[15px] text-foreground flex items-center justify-center gap-2 transition-colors hover:bg-white/[0.07]"
          style={FIELD_STYLE}>
          <UserCircle className="h-5 w-5" />
          Use Face ID
        </button>
      </form>
    </AuthEntryLayout>
  );
}
